import { useEffect, useState } from "react";
import type { AppAdapter } from "../types";
import type { CaiAgentConfig } from "../ai";
import { discoverMcpTools } from "../ai";
import { msalService } from "../auth";
import { envConfig } from "../config";
import { caiApi } from "../api";
import { useSessionStore } from "../store/sessionStore";

interface UseCaiAppOptions {
  adapter: AppAdapter;
  /** Called before MSAL init — e.g., () => Office.onReady() */
  onBeforeInit?: () => Promise<void>;
}

interface UseCaiAppResult {
  ready: boolean;
  error: string | null;
  userName: string | null;
  contextName: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mcpTools: Record<string, any>;
  mcpError: string | null;
  /** Spread-ready props for ChatInterface */
  chatInterfaceProps: {
    adapter: AppAdapter;
    agentConfig: Omit<CaiAgentConfig, "tools" | "modelId" | "maxSteps">;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    mcpTools: Record<string, any>;
    mcpError: string | null;
    userName: string | null;
    contextName: string;
  };
}

/**
 * Consolidates the init sequence shared by all CAi plugins:
 * onBeforeInit → MSAL → caiApi → auth → contextName → session → MCP discovery
 */
export function useCaiApp({
  adapter,
  onBeforeInit,
}: UseCaiAppOptions): UseCaiAppResult {
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [mcpTools, setMcpTools] = useState<Record<string, any>>({});
  const [mcpError, setMcpError] = useState<string | null>(null);
  const [contextName, setContextName] = useState("");
  const [userName, setUserName] = useState<string | null>(null);

  useEffect(() => {
    async function init() {
      try {
        // 1. Run any pre-init (e.g., Office.onReady())
        if (onBeforeInit) {
          await onBeforeInit();
        }

        // 2. Initialize MSAL auth
        await msalService.initialize();

        // 3. Initialize CAi API
        caiApi.initialize(envConfig.apiUrl, () => msalService.getToken());

        // 4. Trigger sign-in and get user name
        try {
          await msalService.getToken();
          const account = msalService.getActiveAccount();
          if (account?.name) {
            setUserName(account.name);
          }
        } catch {
          // Auth will be retried on first message send
        }

        // 5. Get context name from adapter (e.g., document title)
        if (adapter.getContextName) {
          try {
            const name = await adapter.getContextName();
            if (name) setContextName(name);
          } catch {
            // Non-critical
          }
        }

        // 6. Load existing sessions from backend, then create a new one
        // If auth failed (e.g., popups blocked outside Office), session creation
        // will also fail — treat as non-fatal so the UI still loads.
        try {
          await useSessionStore
            .getState()
            .loadSessions([adapter.sessionType]);

          await useSessionStore
            .getState()
            .createSession(adapter.sessionType);

          // 7. Discover MCP tools (non-blocking, only if session was created)
          discoverMcpTools({
            mcpUrl: `${envConfig.apiUrl}/mcp`,
            getToken: () => msalService.getToken(),
            getSessionId: () => useSessionStore.getState().activeSessionId,
          })
            .then(({ tools }) => setMcpTools(tools))
            .catch((err) => {
              setMcpError(
                err instanceof Error ? err.message : "MCP discovery failed"
              );
            });
        } catch {
          // Session will be created on first message when auth succeeds
        }

        setReady(true);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to initialize"
        );
      }
    }

    init();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const agentConfig: Omit<CaiAgentConfig, "tools" | "modelId" | "maxSteps"> = {
    baseURL: envConfig.apiUrl,
    getToken: () => msalService.getToken(),
  };

  return {
    ready,
    error,
    userName,
    contextName,
    mcpTools,
    mcpError,
    chatInterfaceProps: {
      adapter,
      agentConfig,
      mcpTools,
      mcpError,
      userName,
      contextName,
    },
  };
}
